import React from 'react';
import { Layers, TrendingUp, LayoutDashboard, Globe } from 'lucide-react';
import bgImage from '../../assets/home/bg-3.jpg';
import appDemoImage from '../../assets/home/screen-2.png';

const FEATURES = [
  {
    title: 'Aggregated Listings',
    description:
      'Search for any Pokemon card and see every available listing from eBay, TCGplayer, and Canadian card shops in one feed. Compare prices instantly.',
    icon: Layers,
    iconWrapper: 'border border-brand-primary/20 bg-brand-primary/10 group-hover:bg-brand-primary/20',
    iconColor: 'text-brand-hi',
    titleHover: 'group-hover:text-brand-hi',
  },
  {
    title: 'Price History & Intelligence',
    description:
      "Don't just see current listings. See what cards sold for and how prices are trending so you can buy with confidence.",
    icon: TrendingUp,
    iconWrapper: 'border border-chart-green/20 bg-chart-green/10 group-hover:bg-chart-green/20',
    iconColor: 'text-chart-green',
    titleHover: 'group-hover:text-chart-green',
  },
  {
    title: 'Collection Tracker',
    description:
      "Add cards to your collection and watch portfolio value update in real time as market prices move.",
    icon: LayoutDashboard,
    iconWrapper: 'border border-purple/20 bg-purple/10 group-hover:bg-purple/20',
    iconColor: 'text-purple',
    titleHover: 'group-hover:text-purple',
  },
  {
    title: 'Built for Canada',
    description:
      'All prices in CAD, with Canadian card shop listings included, so you can compare accurately without currency conversion noise.',
    icon: Globe,
    iconWrapper: 'border border-skyblue/20 bg-skyblue/10 group-hover:bg-skyblue/20',
    iconColor: 'text-skyblue',
    titleHover: 'group-hover:text-skyblue',
  },
];

export default function SolutionSection() {
  return (
    <section
      style={{ backgroundImage: `url(${bgImage})` }}
      className="relative flex min-h-screen items-center overflow-hidden bg-cover bg-center bg-no-repeat bg-fixed py-24 md:py-32"
    >
      <div className="absolute inset-0 z-0 bg-black/90 backdrop-blur-[8px]" />

      <div className="relative z-20 mx-auto w-full max-w-7xl px-6">
        <div className="mb-16 flex flex-col items-center text-center md:mb-24">
          <h2 className="mb-6 max-w-4xl font-landing-display text-4xl font-bold md:text-5xl lg:text-7xl">
            One Search. Every Listing. <span className="landing-text-gradient">Best Price.</span>
          </h2>
          <p className="max-w-2xl text-xl text-text-secondary">
            Discover tools that make collecting easier and help you find better deals faster.
          </p>
        </div>

        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-3 lg:gap-16">
          <div className="relative z-10 mb-12 flex justify-center lg:col-span-2 lg:mb-0 lg:block lg:-ml-12 xl:-ml-24">
            <div className="w-full max-w-md overflow-hidden rounded-2xl border border-border-base bg-bg-surface shadow-2xl lg:max-w-none">
              <div className="flex h-8 items-center gap-2 border-b border-border-base bg-bg-elevated px-4">
                <div className="h-3 w-3 rounded-full bg-border-hi" />
                <div className="h-3 w-3 rounded-full bg-border-hi" />
                <div className="h-3 w-3 rounded-full bg-border-hi" />
              </div>
              <img src={appDemoImage} alt="Lilycove app demo" className="h-auto w-full object-cover object-top" loading="lazy" />
            </div>
            <div className="absolute left-1/2 top-1/2 -z-10 h-full w-full -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-primary/20 blur-[120px]" />
          </div>

          <div className="lg:col-span-1">
            <div className="flex flex-col gap-10">
              {FEATURES.map((feature) => {
                const Icon = feature.icon;
                return (
                  <div key={feature.title} className="group flex items-start gap-6">
                    <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl transition-all group-hover:scale-110 ${feature.iconWrapper}`}>
                      <Icon className={feature.iconColor} size={28} />
                    </div>
                    <div>
                      <h3 className={`mb-2 text-xl font-bold text-text-primary transition-colors ${feature.titleHover}`}>
                        {feature.title}
                      </h3>
                      <p className="leading-relaxed text-text-secondary">{feature.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
